import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Stack,
} from '../../../components/mui/Core/index'
import PageTitle from '../../../components/PageTitle/page-title'
import PermissionManagementTable from './components/PermissionManagementTable/PermissionManagementTable'
import TableSearch from '../../../components/TableSearch/table-search'
import DeletePermissionModal from './components/DeletePermissionModal/DeletePermissionModal'
import GenericInfoModal from '../../../components/GenericModal/GenericInfoModal'
import Constants from '../../../constants/en.json'
import { fetchAllPermissions } from './view-permission-slice'
import { deletePermission } from './components/UpdatePermission/update-permission-slice'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'

function PermissionManagement() {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const isFetchingAllPermissions = useSelector(
    (state) => state?.permissionManagement?.viewPermission?.isFetchingAllPermissions,
  )
  const isUpdatingPermission = useSelector(
    (state) => state?.permissionManagement?.updatePermission?.isUpdatingPermission,
  )
  const isLoading = isFetchingAllPermissions || isUpdatingPermission

  useEffect(() => {
    dispatch(fetchAllPermissions())
  }, [dispatch, isUpdatingPermission])

  const searchByOptions = [
    { id: 'code', text: 'Permission Code', type: 'string' },
    { id: 'description', text: 'Permission Description', type: 'string' },
    { id: 'application', text: 'Application', type: 'string' },
    { id: 'modifiedBy', text: 'Modified By', type: 'string' },
  ]
  const [tableFilterValue, setTableFilterValue] = useState('')
  const [tableFilterBy, setTableFilterBy] = useState(searchByOptions[0].id)
  const [isDeletePermissionModalOpen, setIsDeletePermissionModalOpen] = useState(false)
  const [selectedPermissionToDelete, setSelectedPermissionToDelete] = useState(null)
  const [isErrorDeleteModalOpen, setIsErrorDeleteModalOpen] = useState(false)

  const returnSearch = (inputValue, selectedMenuItemIndex) => {
    setTableFilterValue(inputValue)
    setTableFilterBy(searchByOptions[selectedMenuItemIndex].id)
  }
  const handleCreatePermission = () => {
    navigate('/role-management/manage-permissions/create-permission')
  }
  const handleExitPermission = () => {
    navigate('/role-management')
  }

  const handleDeletePermissionModalOpen = (permission) => {
    setIsDeletePermissionModalOpen(true)
    setSelectedPermissionToDelete({ ...permission })
  }
  const handleDeletePermissionModalClose = () => {
    setIsDeletePermissionModalOpen(false)
    setSelectedPermissionToDelete(null)
  }
  const handleErrorDeleteModalClose = () => {
    setIsErrorDeleteModalOpen(false)
    setSelectedPermissionToDelete(null)
  }

  const handleConfirmDeletePermission = async () => {
    if (!selectedPermissionToDelete) return

    const permission = {
      name: selectedPermissionToDelete?.code,
      description: selectedPermissionToDelete?.description,
    }

    try {
      setIsDeletePermissionModalOpen(false)
      await dispatch(
        deletePermission({
          permission,
          application: selectedPermissionToDelete?.application,
        }),
      ).unwrap()

      setSelectedPermissionToDelete(null)

      dispatch(
        setSnackbar({
          message:
            Constants.roleManagement.permissionManagement.deletePermission.SUCCESS_DELETE_PERMISSION.replace(
              '__APP__',
              selectedPermissionToDelete?.application,
            ),
          severity: 'success',
          action: true,
          autoHideDuration: 3000,
        }),
      )
    } catch (error) {
      setIsDeletePermissionModalOpen(false)

      if (error?.status === 409) {
        setSelectedPermissionToDelete((prev) => ({
          ...prev,
          apiErrorMessage: error?.message,
        }))
        setIsErrorDeleteModalOpen(true)
        return
      }
      setSelectedPermissionToDelete(null)

      dispatch(
        setSnackbar({
          message:
            Constants.roleManagement.permissionManagement.deletePermission.ERROR_DELETE_PERMISSION.replace(
              '__APP__',
              selectedPermissionToDelete?.application,
            ),
          severity: 'error',
          action: true,
          autoHideDuration: 3000,
        }),
      )
    }
  }

  const buttonStackStyle = {
    marginTop: 2,
    marginBottom: 4,
  }

  return (
    <Box>
      <Box sx={{ flexGrow: 1 }}>
        <Backdrop open={isLoading}>
          <CircularProgress color='inherit' />
        </Backdrop>

        <Container maxWidth='xl'>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <PageTitle
                title={Constants.roleManagement.permissionManagement.PERMISSION_MANAGEMENT}
              />
            </Grid>
          </Grid>

          <Grid
            size={{ xs: 12 }}
            container
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Grid display='flex' justifyContent='left' alignItems='flex-end'>
              <Stack spacing={4} direction='row' container='true' sx={buttonStackStyle}>
                <Button variant='contained' color='secondary' onClick={handleCreatePermission}>
                  {Constants.roleManagement.permissionManagement.CREATE_PERMISSION}
                </Button>
                <Button color='secondary' variant='outlined' onClick={handleExitPermission}>
                  {Constants.roleManagement.permissionManagement.EXIT}
                </Button>
              </Stack>
            </Grid>
            <Grid>
              <TableSearch returnSearch={returnSearch} searchByOptions={searchByOptions} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <PermissionManagementTable
                filterBy={tableFilterBy}
                filterValue={tableFilterValue}
                searchByOptions={searchByOptions}
                handleDeletePermissionModalOpen={handleDeletePermissionModalOpen}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <DeletePermissionModal
        isOpen={isDeletePermissionModalOpen}
        selectedPermissionToDelete={selectedPermissionToDelete}
        handleCloseAction={handleDeletePermissionModalClose}
        onConfirm={handleConfirmDeletePermission}
      />
      <GenericInfoModal
        isOpen={isErrorDeleteModalOpen}
        title={
          Constants.roleManagement.permissionManagement.deletePermission
            .ERROR_DELETE_PERMISSION_TITLE
        }
        content={
          selectedPermissionToDelete?.apiErrorMessage ||
          Constants.roleManagement.permissionManagement.deletePermission
            .ERROR_DELETE_PERMISSION_MESSAGE
        }
        subContent={selectedPermissionToDelete?.code}
        buttonText={Constants.roleManagement.CLOSE}
        handleClose={handleErrorDeleteModalClose}
      />
    </Box>
  )
}

export default PermissionManagement
